import type { ChallengeTodayResponse } from '@cradle7/shared';
import { useSyncExternalStore } from 'react';
import { api } from './api';

/**
 * Phase 2i — daily challenge store. Plain-module + emit pattern mirroring
 * `dailyStatusStore` so that React components can subscribe via
 * `useSyncExternalStore` and bootAuth can hydrate via `setChallenge`.
 *
 * Failure of the underlying fetch is non-fatal: the challenge UI degrades to
 * a "not loaded yet" empty state rather than blocking the boot path.
 */

let cache: ChallengeTodayResponse | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((fn) => fn());
}

export function getChallenge(): ChallengeTodayResponse | null {
  return cache;
}

export function setChallenge(c: ChallengeTodayResponse | null): void {
  cache = c;
  emit();
}

export function subscribeChallenge(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * Self-fetch helper used by `bootAuth` so the challenge cache is warm by the
 * time the player opens the Daily Challenge section. Swallows errors — the
 * caller should already be tolerating null.
 */
export async function hydrateChallenge(): Promise<void> {
  try {
    const resp = await api.getChallenge();
    cache = resp;
    emit();
  } catch (err) {
    // Non-fatal: challenge UI will show its empty state.
    console.warn('[challengeStore] hydrate failed', err);
  }
}

/** Test hook: clear in-memory state + listeners. */
export function _resetChallengeStore(): void {
  cache = null;
  listeners.clear();
}

let snapshotKey: ChallengeTodayResponse | null = null;
let snapshotCache: ChallengeTodayResponse | null = null;

function getSnapshot(): ChallengeTodayResponse | null {
  if (snapshotKey === cache) return snapshotCache;
  snapshotKey = cache;
  snapshotCache = cache;
  return cache;
}

export function useChallenge(): ChallengeTodayResponse | null {
  return useSyncExternalStore(subscribeChallenge, getSnapshot, getSnapshot);
}
